import React from 'react';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import {
  Box, Typography, IconButton, Divider, Tooltip, Stack, Button,
  Switch, FormControlLabel, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import NightsStayRoundedIcon from '@mui/icons-material/NightsStayRounded';
import CheckIcon from '@mui/icons-material/Check';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TableRowsIcon from '@mui/icons-material/TableRows';

const DRAWER_WIDTH = 300;

const presets = [
  { id: 1, label: 'Verde',   menuColor: '#00A76F', modalColor: '#00A76F', buttonColor: '#004D40' },
  { id: 2, label: 'Azul',    menuColor: '#078DEE', modalColor: '#078DEE', buttonColor: '#01579B' },
  { id: 3, label: 'Violeta', menuColor: '#7635DC', modalColor: '#7635DC', buttonColor: '#4A148C' },
  { id: 4, label: 'Navy',    menuColor: '#0A395F', modalColor: '#0A395F', buttonColor: '#1A237E' },
  { id: 5, label: 'Naranja', menuColor: '#FDA92D', modalColor: '#FDA92D', buttonColor: '#E65100' },
  { id: 6, label: 'Rojo',    menuColor: '#FF3030', modalColor: '#FF3030', buttonColor: '#B71C1C' },
];

/* ── Sub-componentes ─────────────────────────────────────────────────────── */

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
      color: 'text.disabled', display: 'block', mb: 1,
      fontFamily: '"Public Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const ColorDot = ({ color, selected, onClick, tooltip }) => (
  <Tooltip title={tooltip || ''} placement="top">
    <Box
      onClick={onClick}
      sx={{
        width: 36, height: 36, borderRadius: '50%',
        bgcolor: color, cursor: 'pointer', flexShrink: 0,
        border: selected ? '2px solid' : '2px solid transparent',
        borderColor: selected ? 'text.primary' : 'transparent',
        outline: '3px solid',
        outlineColor: selected ? color : 'transparent',
        outlineOffset: '2px',
        transition: 'all 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover': { transform: 'scale(1.15)' },
      }}
    >
      {selected && <CheckIcon sx={{ fontSize: 16, color: 'white' }} />}
    </Box>
  </Tooltip>
);

const ColorPickerRow = ({ label, value, onChange, id }) => (
  <Box
    component="label"
    htmlFor={id}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      py: 1.25, cursor: 'pointer', userSelect: 'none',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontFamily: '"Public Sans", sans-serif' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 26, height: 26, borderRadius: 1,
          bgcolor: value, border: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}
      />
      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontFamily: '"Public Sans", sans-serif' }}>
        Cambiar
      </Typography>
      <input
        id={id}
        type="color"
        value={value}
        onChange={onChange}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </Box>
  </Box>
);

/* ── ModalConfig ─────────────────────────────────────────────────────────── */

function ModalConfig({ open, onClose, onThemeChange, onColorChange, aiEnabled, onAiEnabledChange, aiMode, onAiModeChange }) {
  const theme = useTheme();

  const defaultColor = theme.backgroundColorDefault?.default || '#0A395F';

  const [menuColor, setMenuColor]   = React.useState(localStorage.getItem('menuColor')   || defaultColor);
  const [modalColor, setModalColor] = React.useState(localStorage.getItem('modalColor')  || defaultColor);
  const [buttonColor, setButtonColor] = React.useState(localStorage.getItem('buttonColor') || defaultColor);
  const [selectedPreset, setSelectedPreset] = React.useState(null);
  const [selectedMode, setSelectedMode] = React.useState(localStorage.getItem('themeMode') || 'light');

  React.useEffect(() => {
    const savedPreset = localStorage.getItem('selectedPreset');
    if (savedPreset) {
      try { setSelectedPreset(JSON.parse(savedPreset)); } catch (_) { /* noop */ }
    }
  }, []);

  const handleDarkModeChange = (mode) => {
    setSelectedMode(mode);
    onThemeChange(mode);
    localStorage.setItem('themeMode', mode);
  };

  const handleMenuColorChange = (e) => {
    const color = e.target.value;
    setMenuColor(color);
    setSelectedPreset(null);
    onColorChange('menu', color);
    localStorage.setItem('menuColor', color);
  };

  const handleModalColorChange = (e) => {
    const color = e.target.value;
    setModalColor(color);
    setSelectedPreset(null);
    onColorChange('modal', color);
    localStorage.setItem('modalColor', color);
  };

  const handleButtonColorChange = (e) => {
    const color = e.target.value;
    setButtonColor(color);
    setSelectedPreset(null);
    onColorChange('button', color);
    localStorage.setItem('buttonColor', color);
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    localStorage.setItem('selectedPreset', JSON.stringify(preset));
    setMenuColor(preset.menuColor);
    setModalColor(preset.modalColor);
    setButtonColor(preset.buttonColor);
    localStorage.setItem('menuColor',   preset.menuColor);
    localStorage.setItem('modalColor',  preset.modalColor);
    localStorage.setItem('buttonColor', preset.buttonColor);
    onColorChange('menu',   preset.menuColor);
    onColorChange('modal',  preset.modalColor);
    onColorChange('button', preset.buttonColor);
  };

  const resetToDefaults = () => {
    setSelectedMode('light');
    setMenuColor(defaultColor);
    setModalColor(defaultColor);
    setButtonColor(defaultColor);
    setSelectedPreset(null);
    localStorage.removeItem('selectedPreset');
    localStorage.removeItem('menuColor');
    localStorage.removeItem('modalColor');
    localStorage.removeItem('buttonColor');
    localStorage.setItem('themeMode', 'light');
    onThemeChange('light');
    onColorChange('menu',   defaultColor);
    onColorChange('modal',  defaultColor);
    onColorChange('button', defaultColor);
    // reset AI defaults
    onAiEnabledChange(true);
    onAiModeChange('floating');
  };

  const headerBg = theme.backgroundDrawer?.default || defaultColor;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
    >
      {/* ── Encabezado ── */}
      <Box
        sx={{
          px: 2.5, py: 1.75,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: headerBg,
          color: 'white',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, fontSize: '0.9rem', fontFamily: '"Public Sans", sans-serif' }}
        >
          Configuración de estilos
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Restablecer">
            <IconButton
              size="small"
              onClick={resetToDefaults}
              sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white' } }}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Cuerpo ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, bgcolor: 'background.paper' }}>

        {/* Modo */}
        <SectionLabel>Modo</SectionLabel>
        <Stack direction="row" spacing={1.5}>
          <Button
            fullWidth
            variant={selectedMode === 'light' ? 'contained' : 'outlined'}
            startIcon={<WbSunnyRoundedIcon />}
            onClick={() => handleDarkModeChange('light')}
            sx={{ borderRadius: 2, py: 1.25, textTransform: 'none', fontWeight: 600, fontFamily: '"Public Sans", sans-serif' }}
          >
            Claro
          </Button>
          <Button
            fullWidth
            variant={selectedMode === 'dark' ? 'contained' : 'outlined'}
            startIcon={<NightsStayRoundedIcon />}
            onClick={() => handleDarkModeChange('dark')}
            sx={{ borderRadius: 2, py: 1.25, textTransform: 'none', fontWeight: 600, fontFamily: '"Public Sans", sans-serif' }}
          >
            Oscuro
          </Button>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* Presets */}
        <SectionLabel>Presets de color</SectionLabel>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
          {presets.map((preset) => (
            <ColorDot
              key={preset.id}
              color={preset.menuColor}
              selected={selectedPreset?.id === preset.id}
              onClick={() => handlePresetSelect(preset)}
              tooltip={preset.label}
            />
          ))}
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Personalizado */}
        <SectionLabel>Personalizado</SectionLabel>
        <Box>
          <ColorPickerRow
            label="Menú y AppBar"
            value={menuColor}
            onChange={handleMenuColorChange}
            id="cfg-color-menu"
          />
          <Divider />
          <ColorPickerRow
            label="Diálogos"
            value={modalColor}
            onChange={handleModalColorChange}
            id="cfg-color-modal"
          />
          <Divider />
          <ColorPickerRow
            label="Botones"
            value={buttonColor}
            onChange={handleButtonColorChange}
            id="cfg-color-button"
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Asistente IA */}
        <SectionLabel>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <SmartToyIcon sx={{ fontSize: 13 }} />
            <span>Asistente IA</span>
          </Stack>
        </SectionLabel>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={aiEnabled}
                onChange={(e) => onAiEnabledChange(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontFamily: '"Public Sans", sans-serif' }}>
                {aiEnabled ? 'Habilitado' : 'Deshabilitado'}
              </Typography>
            }
            sx={{ ml: 0, mb: 1.5 }}
          />

          {aiEnabled && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontFamily: '"Public Sans", sans-serif' }}>
                Presentación
              </Typography>
              <ToggleButtonGroup
                value={aiMode}
                exclusive
                onChange={(_, val) => { if (val) onAiModeChange(val); }}
                size="small"
                fullWidth
              >
                <ToggleButton value="floating" sx={{ textTransform: 'none', gap: 0.5, fontSize: '0.78rem' }}>
                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                  Flotante
                </ToggleButton>
                <ToggleButton value="tab" sx={{ textTransform: 'none', gap: 0.5, fontSize: '0.78rem' }}>
                  <TableRowsIcon sx={{ fontSize: 15 }} />
                  Pestaña
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>

      </Box>
    </Drawer>
  );
}

export default ModalConfig;
